// LBD-CUSTOM: Design showcase — design card front/back switch, tab state, and keeping ?tab / ?view across facet updates
import { ThemeEvents } from '@theme/events';

class DesignShowcase extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#applyTab();

    // The section is morphed in place after facet updates; re-apply the tab state afterwards.
    this.#observer = new MutationObserver(() => this.#applyTab());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#observer?.disconnect();
  }

  get #isAllTab() {
    return document.documentElement.dataset.lbdTab === 'all';
  }

  #applyTab() {
    const forcedAll = this.hasAttribute('data-has-filters') && !this.hasAttribute('data-landing');
    const current = this.#isAllTab || forcedAll ? 'all' : 'designs';

    for (const tab of this.querySelectorAll('[data-ds-tab]')) {
      const isCurrent = tab.getAttribute('data-ds-tab') === current;
      if (isCurrent && tab.getAttribute('aria-current') !== 'page') tab.setAttribute('aria-current', 'page');
      if (!isCurrent && tab.hasAttribute('aria-current')) tab.removeAttribute('aria-current');
    }
  }

  /**
   * Facet changes rebuild the URL from the filter form only; put ?tab=all and ?view back before the section re-renders.
   */
  #handleFilterUpdate = () => {
    const url = new URL(window.location.href);
    let changed = false;

    if (this.#isAllTab && url.searchParams.get('tab') !== 'all') {
      url.searchParams.set('tab', 'all');
      changed = true;
    }

    const view = this.dataset.viewParam;
    if (view && url.searchParams.get('view') !== view) {
      url.searchParams.set('view', view);
      changed = true;
    }

    if (changed) history.replaceState(history.state, '', url.toString());
  };

  /**
   * Front/Back switch: swaps the main and inset art without navigating.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.design-card__switch button[data-view]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();

    const art = button.closest('.design-card__art');
    if (!(art instanceof HTMLElement)) return;

    const view = button.dataset.view === 'back' ? 'back' : 'front';
    const other = view === 'back' ? 'front' : 'back';
    const mainSrc = art.dataset[`${view}Src`];
    if (!mainSrc) return;

    const main = art.querySelector('.design-card__main');
    const inset = art.querySelector('.design-card__inset img');
    const insetLabel = art.querySelector('.design-card__inset-label');
    const otherSrc = art.dataset[`${other}Src`];

    if (main instanceof HTMLImageElement) main.src = mainSrc;
    if (inset instanceof HTMLImageElement && otherSrc) inset.src = otherSrc;
    if (insetLabel) insetLabel.textContent = art.dataset[`${other}Label`] ?? '';

    art.dataset.view = view;
    for (const b of art.querySelectorAll('.design-card__switch button')) {
      b.setAttribute('aria-pressed', String(b === button));
    }
  };
}

if (!customElements.get('design-showcase')) {
  customElements.define('design-showcase', DesignShowcase);
}
